import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';


const ChatQueue = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [activeChats, setActiveChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalConversations: 0, unreadCount: 0, activeCount: 0 });
  const [confirmDelete, setConfirmDelete] = useState(null); // chatId to confirm delete
  const [actionLoading, setActionLoading] = useState(null); // chatId being actioned

  // Store institute user_id in a ref so subscription callbacks can use it
  const instituteUserIdRef = useRef(null);

  const loadChats = useCallback(async (instUserId) => {
    try {
      const { data: conversations, error } = await supabase?.from('chat_conversations')?.select(`
          id,
          status,
          handler_type,
          last_message_at,
          created_at,
          student:student_id(id, name, email)
        `)?.eq('institute_id', instUserId)?.eq('status', 'active')?.order('last_message_at', { ascending: false })?.limit(10);

      if (error) throw error;

      // Fetch latest message for each conversation
      const chatsWithMessages = await Promise.all(
        (conversations || [])?.map(async (conv) => {
          const { data: messages } = await supabase?.from('chat_messages')?.select('message, sent_at, sender_type')?.eq('conversation_id', conv?.id)?.order('sent_at', { ascending: false })?.limit(1);

          const lastMsg = messages?.[0];
          const lastMessageAt = conv?.last_message_at
            ? new Date(conv.last_message_at)
            : new Date(conv.created_at);
          const diffMs = Date.now() - lastMessageAt?.getTime();
          const diffMins = Math.floor(diffMs / 60000);
          const timestamp =
            diffMins < 1
              ? 'Just now'
              : diffMins < 60
              ? `${diffMins}m ago`
              : `${Math.floor(diffMins / 60)}h ago`;

          return {
            id: conv?.id,
            studentName: conv?.student?.name || 'Student',
            lastMessage: lastMsg?.message || 'Started a conversation',
            timestamp,
            handlerType: conv?.handler_type,
            lastMessageAt: conv?.last_message_at,
          };
        })
      );

      setActiveChats(chatsWithMessages);

      // Stats
      const { count: totalCount } = await supabase?.from('chat_conversations')?.select('id', { count: 'exact', head: true })?.eq('institute_id', instUserId);

      const { count: activeCount } = await supabase?.from('chat_conversations')?.select('id', { count: 'exact', head: true })?.eq('institute_id', instUserId)?.eq('status', 'active');

      setStats({
        totalConversations: totalCount || 0,
        activeCount: activeCount || 0,
        unreadCount: chatsWithMessages?.length || 0,
      });
    } catch (err) {
      console.error('Error loading chats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.id || !userProfile) return;

    let channel = null;

    const init = async () => {
      try {
        const instituteProfile = userProfile?.institute_profiles?.[0];
        if (!instituteProfile) { setLoading(false); return; }

        const { data: instUser } = await supabase?.from('institute_profiles')?.select('user_id')?.eq('id', instituteProfile?.id)?.single();

        if (!instUser?.user_id) { setLoading(false); return; }

        instituteUserIdRef.current = instUser?.user_id;

        // Initial load
        await loadChats(instUser?.user_id);

        // Subscribe to new messages — reload chat list when a message arrives
        channel = supabase?.channel(`chat_queue_${instUser?.user_id}`)?.on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'chat_messages',
            },
            async (payload) => {
              const convId = payload?.new?.conversation_id;
              if (!convId) return;

              const { data: conv } = await supabase?.from('chat_conversations')?.select('institute_id')?.eq('id', convId)?.single();

              if (conv?.institute_id === instituteUserIdRef?.current) {
                await loadChats(instituteUserIdRef?.current);
              }
            }
          )?.on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'chat_conversations',
              filter: `institute_id=eq.${instUser?.user_id}`,
            },
            async () => {
              await loadChats(instituteUserIdRef?.current);
            }
          )?.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              console.log(`[ChatQueue] ✅ Realtime subscribed`);
            }
          });
      } catch (err) {
        console.error('ChatQueue init error:', err);
        setLoading(false);
      }
    };

    init();

    return () => {
      if (channel) supabase?.removeChannel(channel);
    };
  }, [user?.id, userProfile, loadChats]);

  const handleArchive = async (e, chatId) => {
    e?.stopPropagation();
    try {
      setActionLoading(chatId);
      await supabase?.from('chat_conversations')?.update({ status: 'closed' })?.eq('id', chatId);
      setActiveChats((prev) => prev?.filter((c) => c?.id !== chatId));
      setStats((prev) => ({ ...prev, activeCount: Math.max(0, prev?.activeCount - 1) }));
    } catch (err) {
      console.error('Error archiving chat:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      setActionLoading(confirmDelete);
      // Delete messages first (foreign key), then conversation
      await supabase?.from('chat_messages')?.delete()?.eq('conversation_id', confirmDelete);
      await supabase?.from('chat_conversations')?.delete()?.eq('id', confirmDelete);
      setActiveChats((prev) => prev?.filter((c) => c?.id !== confirmDelete));
      setStats((prev) => ({
        ...prev,
        activeCount: Math.max(0, prev?.activeCount - 1),
        totalConversations: Math.max(0, prev?.totalConversations - 1),
      }));
    } catch (err) {
      console.error('Error deleting chat:', err);
    } finally {
      setActionLoading(null);
      setConfirmDelete(null);
    }
  };

  const getHandlerColor = (handlerType) =>
    handlerType === 'human' ? 'border-l-primary' : 'border-l-accent';

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Active Chats
              </h2>
              <p className="text-sm text-muted-foreground">
                <span className="data-text">{stats?.activeCount}</span> active conversations
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="left"
            onClick={() => navigate('/chat-interface')}
          >
            View All
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading chats...</p>
        </div>
      ) : activeChats?.length === 0 ? (
        <div className="p-8 text-center">
          <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No active conversations yet.</p>
        </div>
      ) : (
        <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
          {activeChats?.map((chat) => (
            <div
              key={chat?.id}
              className={`p-4 hover:bg-muted/50 transition-smooth cursor-pointer border-l-4 ${getHandlerColor(chat?.handlerType)}`}
              onClick={() => navigate('/chat-interface')}
            >
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-base font-bold text-primary">
                      {chat?.studentName?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  {/* Live indicator dot */}
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-foreground truncate">{chat?.studentName}</h3>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{chat?.timestamp}</span>
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {chat?.lastMessage}
                  </p>

                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        chat?.handlerType === 'human' ?'bg-primary/10 text-primary' :'bg-accent/10 text-accent'
                      }`}
                    >
                      {chat?.handlerType === 'human' ? 'Human' : 'AI'}
                    </span>

                    <div className="flex items-center gap-1">
                      {/* Archive button */}
                      <button
                        title="Archive chat"
                        disabled={actionLoading === chat?.id}
                        onClick={(e) => handleArchive(e, chat?.id)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-warning hover:bg-warning/10 transition-smooth disabled:opacity-50"
                      >
                        <Icon name="Archive" size={15} />
                      </button>
                      {/* Delete button */}
                      <button
                        title="Delete chat"
                        disabled={actionLoading === chat?.id}
                        onClick={(e) => { e?.stopPropagation(); setConfirmDelete(chat?.id); }}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-smooth disabled:opacity-50"
                      >
                        <Icon name="Trash2" size={15} />
                      </button>
                      <Button
                        variant="default"
                        size="sm"
                        iconName="MessageSquare"
                        iconPosition="left"
                        onClick={(e) => {
                          e?.stopPropagation();
                          navigate('/chat-interface');
                        }}
                      >
                        Reply
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} color="var(--color-destructive)" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete Chat</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              All messages in this conversation will be permanently deleted. Are you sure you want to continue?
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(null)}
                disabled={actionLoading === confirmDelete}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={handleDeleteConfirm}
                loading={actionLoading === confirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatQueue;